"use client";

import { useRouterState } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useTranslations } from "use-intl";

import { EmailVerificationBannerContent } from "@/components/email-verification-banner-content";
import { emailVerificationTarget } from "@/components/email-verification-target";
import { useSession } from "@/hooks/use-session";
import { authClient } from "@/lib/auth-client";
import { getRedirectPathFromLocation } from "@/lib/redirect-after-login";

const DISMISS_MS = 24 * 60 * 60 * 1000;

function dismissKey(userId: string) {
  return `garimpa.emailVerificationBanner.dismissedUntil.${userId}`;
}

function isDismissed(userId: string) {
  if (typeof window === "undefined") return true;
  const dismissedUntil = Number(window.localStorage.getItem(dismissKey(userId)));
  return Number.isFinite(dismissedUntil) && dismissedUntil > Date.now();
}

export function EmailVerificationBanner() {
  const t = useTranslations("globals.auth");
  const { data: session } = useSession();
  const location = useRouterState({ select: (state) => state.location });
  const [dismissed, setDismissed] = useState(true);
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);
  const target = emailVerificationTarget(session?.user);
  const targetUserId = target?.userId;

  useEffect(() => {
    if (!targetUserId) return;
    setDismissed(isDismissed(targetUserId));
  }, [targetUserId]);

  if (!target || dismissed) return null;
  const { email, userId } = target;

  const handleDismiss = () => {
    window.localStorage.setItem(dismissKey(userId), String(Date.now() + DISMISS_MS));
    setDismissed(true);
  };

  const handleResend = async () => {
    setResending(true);
    setResent(false);
    try {
      const result = await authClient.sendVerificationEmail({
        email,
        callbackURL: getRedirectPathFromLocation(location) ?? "/",
      });

      if (result?.error) {
        toast.error(result.error.message || result.error.statusText);
        return;
      }

      setResent(true);
      toast.success(t("verificationResent"));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Unable to resend verification email");
    } finally {
      setResending(false);
    }
  };

  return (
    <EmailVerificationBannerContent
      email={email}
      onDismiss={handleDismiss}
      onResend={handleResend}
      resending={resending}
      resent={resent}
    />
  );
}
